<template>
  <!-- KSK008 — kiosk menu item card. The root keeps the `item-img-*` class that
       Order.vue's addCartAnimation() looks up (it clones the first <img>).
       KSK072 — tapping the image opens the detail popup (every item, incl. ones
       with no MOG — KSK042's tap-to-add is gone); "+" / count / stepper act as
       before; the rest of the card does nothing. -->
  <article
    class="ksk-item-card"
    :class="[animClass, { 'ksk-item-card--sold-out': !available }]"
    v-bind="kskSlot('item_card')"
  >
    <div
      class="ksk-item-card__media"
      :class="{ 'ksk-item-card__media--tappable': available }"
      @click="available && $emit('open')"
    >
      <img :src="item.image" alt="" />
      <!-- KSK064: merchant-chosen icon badge (chef hat, "!", …). -->
      <span v-if="hasIcon" class="ksk-item-card__icon">
        <KskIcon :name="item.kiosk_icon" />
      </span>
      <span v-if="!available" class="ksk-item-card__sold-out">{{ $t("kiosk.itemCard.soldOut") }}</span>
    </div>

    <h3 class="ksk-item-card__name">{{ translatedName(item.item_name) }}</h3>
    <p class="ksk-item-card__desc">{{ item.description }}</p>

    <div class="ksk-item-card__footer">
      <div class="ksk-item-card__prices">
        <template v-if="item.is_by_weight">
          <span class="ksk-item-card__price ksk-item-card__price--weight">
            <span class="material-icons-round">scale</span>
            {{ weightPriceText ? $t("kiosk.itemCard.per100g", { price: weightPriceText }) : $t("kiosk.itemCard.soldByWeight") }}
          </span>
        </template>
        <template v-else-if="hasMemberDiscount && isLogined">
          <span class="ksk-item-card__price ksk-item-card__price--struck">
            {{ priceText }}
          </span>
          <span class="ksk-item-card__member-pill">
            {{ $t("kiosk.itemCard.memberPrice", { price: memberPriceText }) }}
          </span>
        </template>
        <template v-else>
          <!-- KSK064: merchant's "was" price, crossed out above the real one. -->
          <span v-if="strikePriceText" class="ksk-item-card__price--struck">{{ strikePriceText }}</span>
          <!-- A Choice item shows its first choice (picked by default), a Sub Category its
               cheapest item (picked by default), so there is no "From". -->
          <span class="ksk-item-card__price">{{ priceText }}</span>
          <!-- KSK119: "Special Price | 10AM to 5PM" while a price schedule sells it below its usual price. -->
          <span v-if="specialPriceText" class="ksk-item-card__special">{{ specialPriceText }}</span>
          <span v-if="hasMemberDiscount" class="ksk-item-card__member-hint">
            <!-- KSK093: the member price itself, in brackets. -->
            {{ $t("kiosk.itemCard.memberAvailablePrice", { price: memberPriceText }) }}
          </span>
        </template>
      </div>

      <div v-if="available" class="ksk-item-card__action">
        <!-- Items with options / by weight: "+" (or the count once in the
             cart) opens the detail sheet. -->
        <template v-if="needsDetail">
          <button
            v-if="quantity > 0"
            type="button"
            class="ksk-item-card__count"
            :aria-label="$t('kiosk.itemCard.add')"
            @click.stop="$emit('open')"
          >
            {{ quantity > 99 ? "99+" : quantity }}
          </button>
          <button
            v-else
            type="button"
            class="ksk-item-card__add"
            :aria-label="$t('kiosk.itemCard.add')"
            @click.stop="$emit('open')"
          >
            <span class="material-icons-round">add</span>
          </button>
        </template>
        <template v-else>
          <KskStepper
            v-if="quantity > 0"
            :modelValue="quantity"
            @click.stop
            @decrement="$emit('change-quantity', 1)"
            @increment="$emit('change-quantity', 2)"
          />
          <button
            v-else
            type="button"
            class="ksk-item-card__add"
            :aria-label="$t('kiosk.itemCard.add')"
            @click.stop="$emit('add')"
          >
            <span class="material-icons-round">add</span>
          </button>
        </template>
      </div>
    </div>
  </article>
</template>

<script>
import KskStepper from "./ui/KskStepper.vue";
import KskIcon from "./ui/KskIcon.vue";
import { kskSlot } from "@/config/kioskColorSlots";
import { isKioskIcon } from "@/config/kioskIcons";
import { translatedName } from "@/i18n/menuTranslate";
import { specialPriceLabel } from "@/config/specialPrice";

export default {
  name: "KskItemCard",
  components: { KskStepper, KskIcon },
  // open            → image tapped, or "+" / count on an item with options
  //                   (detail / weighing)
  // add             → "+" on an item without options (adds 1)
  // change-quantity → stepper: 1 = minus, 2 = plus
  emits: ["open", "add", "change-quantity"],
  props: {
    item: { type: Object, required: true },
    animClass: { type: String, default: "" },
    quantity: { type: Number, default: 0 },
    available: { type: Boolean, default: true },
    isLogined: { type: Boolean, default: false },
    hasMemberDiscount: { type: Boolean, default: false },
    priceText: { type: String, default: "" },
    memberPriceText: { type: String, default: "" },
    weightPriceText: { type: String, default: "" },
    // KSK064: empty unless the merchant's strike-off price beats the price shown.
    strikePriceText: { type: String, default: "" },
  },
  computed: {
    needsDetail() {
      return this.item.isHaveModifierOptionGroups > 0 || !!this.item.is_by_weight;
    },
    hasIcon() {
      return isKioskIcon(this.item.kiosk_icon);
    },
    specialPriceText() {
      return specialPriceLabel(this.item, this.$t, this.isLogined);
    },
  },
  methods: {
    kskSlot,
    translatedName,
  },
};
</script>

<style scoped>
.ksk-item-card {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: var(--ksk-space-2);
  border-radius: var(--ksk-radius-card);
  background: var(--ksk-surface);
  /* KSK063: item_card colour slot (outline = card ring, font = item name,
     accent = price + add button); theme values otherwise. */
  box-shadow: var(--ksk-shadow-card), inset 0 0 0 2px var(--ksk-slot-outline, transparent);
  font-family: var(--ksk-font);
  color: var(--ksk-slot-font, var(--ksk-text));
  text-align: left;
  -webkit-tap-highlight-color: transparent;
}

.ksk-item-card__media {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 16px;
  overflow: hidden;
  background: var(--ksk-surface-muted);
}
.ksk-item-card__media--tappable {
  cursor: pointer;
}
/* KSK064: icon badge, top-left of the image (sold-out label stays centred). */
.ksk-item-card__icon {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--ksk-warning);
  color: var(--ksk-on-status);
  font-size: 30px;
  box-shadow: var(--ksk-shadow-card);
  pointer-events: none;
}
.ksk-item-card__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ksk-item-card__name {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  min-height: calc(var(--ksk-item-lh) * 2);
  margin: 12px 0 0;
  font-family: var(--ksk-font);
  font-size: var(--ksk-item-size);
  line-height: var(--ksk-item-lh);
  font-weight: 600;
  color: inherit;
  word-break: break-word;
}
.ksk-item-card__desc {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  min-height: calc(var(--ksk-caption-lh) * 2);
  margin: 4px 0 0;
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  color: var(--ksk-text-secondary);
}

.ksk-item-card__footer {
  margin-top: auto;
  padding-top: var(--ksk-space-2);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px var(--ksk-space-1);
  min-height: 80px;
}
.ksk-item-card__prices {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
}
.ksk-item-card__price {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--ksk-price-size);
  line-height: var(--ksk-price-lh);
  font-weight: 700;
  color: var(--ksk-slot-accent, var(--ksk-primary));
  white-space: nowrap;
}
.ksk-item-card__price--struck {
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  font-weight: 500;
  color: var(--ksk-text-secondary);
  text-decoration: line-through;
}
.ksk-item-card__price--weight .material-icons-round {
  font-size: 28px;
}
.ksk-item-card__member-pill {
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 14px;
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-slot-accent, var(--ksk-primary));
  color: var(--ksk-on-primary);
  font-size: var(--ksk-caption-size);
  font-weight: 700;
  white-space: nowrap;
}
/* KSK119: Special Scheduled Price tag, accent colour so it reads as an offer. */
.ksk-item-card__special {
  font-size: 16px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ksk-slot-accent, var(--ksk-primary));
}
.ksk-item-card__member-hint {
  font-size: 16px;
  line-height: 20px;
  font-style: italic;
  color: var(--ksk-text-secondary);
}

.ksk-item-card__action {
  flex-shrink: 0;
  margin-left: auto;
}
.ksk-item-card__add,
.ksk-item-card__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border: none;
  border-radius: 50%;
  background: var(--ksk-slot-accent, var(--ksk-primary));
  color: var(--ksk-on-primary);
  box-shadow: var(--ksk-shadow-card);
  cursor: pointer;
}
.ksk-item-card__add .material-icons-round {
  font-size: 32px;
}
.ksk-item-card__count {
  background: color-mix(in srgb, var(--ksk-slot-accent, var(--ksk-primary)) 12%, #ffffff);
  color: var(--ksk-slot-accent, var(--ksk-primary));
  font-size: var(--ksk-label-size);
  font-weight: 700;
}
.ksk-item-card__add:active,
.ksk-item-card__count:active {
  transform: scale(0.95);
}

/* Sold out: greyed, no "+" rendered. */
.ksk-item-card--sold-out .ksk-item-card__media img {
  filter: grayscale(1);
  opacity: 0.6;
}
.ksk-item-card--sold-out .ksk-item-card__name,
.ksk-item-card--sold-out .ksk-item-card__price {
  color: var(--ksk-text-secondary);
}
.ksk-item-card__sold-out {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 8px 20px;
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-error);
  color: var(--ksk-on-status);
  font-size: var(--ksk-label-size);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
</style>
